import type { DesignSpec } from "@/types/designSpec";
import type { CreatePageState } from "@/stores/createPageStore";
import { FormPersistenceService } from "@/services/formPersistenceService";
import {
  prepareQuoteAuthRedirectPayload,
  prepareSavePointPayload,
} from "@/hooks/create-page/utils/formStateHelpers";
import SavePoint from "@/utils/savePoint";
import {
  clearQuoteResumeAfterAuth,
  QUOTE_RESUME_ID_PARAM,
  rememberQuoteResumeAfterAuth,
} from "@/utils/quoteResumePersistence";
import { createResumeIntentKey } from "@/utils/createResumeIntentKey";
import { sanitizeCreateReturnUrl } from "@/utils/creditCheckoutReturnUrl";
import { CREATE_AUTH_HANDOFF_PARAM } from "@/utils/createAuthHandoffPersistence";
import {
  beginTrackedCreateAuthHandoff,
  cancelTrackedCreateAuthHandoff,
} from "@/utils/createAuthHandoffLifecycle";

export const QUOTE_AUTH_HANDOFF_ERROR =
  "Your dress is still here. Allow this site to save data in your browser, then try signing in again.";

export interface PreparedQuoteAuthHandoff {
  intentId: string;
  returnPath: string;
}

function persistExactQuoteHandoff({
  state,
  data,
  preferredIntentId,
  designSpecOverride,
  quoteScope,
}: {
  state: CreatePageState;
  data: ReturnType<typeof prepareSavePointPayload>;
  preferredIntentId?: string | null;
  designSpecOverride?: DesignSpec;
  quoteScope?: "front_only";
}): string | null {
  if (quoteScope === "front_only" && !designSpecOverride) return null;
  const resumeKey = createResumeIntentKey({
    ticketId: state.ticket.id,
    frontImagePath: state.generation.images.front.path,
    agentSessionId: state.agent.sessionId,
  });
  const intentId = rememberQuoteResumeAfterAuth(
    resumeKey,
    preferredIntentId,
    designSpecOverride,
    quoteScope
  );
  if (!intentId) return null;

  FormPersistenceService.saveState({
    data,
    source: "auth_redirect",
    intentId,
  });

  // Email confirmation commonly opens a new tab. Refuse to leave this dress
  // unless the exact scoped state made it to localStorage, rather than merely
  // surviving in this tab's sessionStorage.
  const durableSavePoint = SavePoint.getDurableSavePoint(intentId);
  if (
    durableSavePoint?.intentId !== intentId ||
    durableSavePoint.source !== "auth_redirect"
  ) {
    clearQuoteResumeAfterAuth(intentId);
    SavePoint.clear(intentId);
    return null;
  }

  return intentId;
}

function buildQuoteReturnPath({
  currentHref,
  intentId,
  authHandoffId,
}: {
  currentHref: string;
  intentId: string;
  authHandoffId?: string | null;
}): string {
  const returnUrl = sanitizeCreateReturnUrl(currentHref);
  returnUrl.searchParams.set("quote_intent", "1");
  returnUrl.searchParams.set(QUOTE_RESUME_ID_PARAM, intentId);
  if (authHandoffId) {
    returnUrl.searchParams.set(CREATE_AUTH_HANDOFF_PARAM, authHandoffId);
  }
  return returnUrl.pathname + returnUrl.search + returnUrl.hash;
}

/** Prepare the exact continuation used by an in-place anonymous upgrade. */
export function prepareQuoteAccountUpgradeHandoff(
  state: CreatePageState,
  currentHref: string,
  designSpecOverride?: DesignSpec,
  quoteScope?: "front_only"
): PreparedQuoteAuthHandoff | null {
  const intentId = persistExactQuoteHandoff({
    state,
    data: prepareSavePointPayload(state, 1),
    designSpecOverride,
    quoteScope,
  });
  if (!intentId) return null;

  return {
    intentId,
    returnPath: buildQuoteReturnPath({ currentHref, intentId }),
  };
}

/** Prepare the exact continuation used by existing-account or Google sign-in. */
export function prepareQuoteSignInHandoff({
  state,
  currentHref,
  sourceUserId,
  designSpecOverride,
  quoteScope,
}: {
  state: CreatePageState;
  currentHref: string;
  sourceUserId?: string | null;
  designSpecOverride?: DesignSpec;
  quoteScope?: "front_only";
}): PreparedQuoteAuthHandoff | null {
  const authHandoffId = beginTrackedCreateAuthHandoff(sourceUserId, {
    allowUnowned: !sourceUserId,
  });
  if (!authHandoffId) return null;

  const intentId = persistExactQuoteHandoff({
    state,
    data: prepareQuoteAuthRedirectPayload(state),
    preferredIntentId: authHandoffId,
    designSpecOverride,
    quoteScope,
  });
  if (!intentId) {
    cancelTrackedCreateAuthHandoff(authHandoffId);
    return null;
  }

  return {
    intentId,
    returnPath: buildQuoteReturnPath({
      currentHref,
      intentId,
      authHandoffId,
    }),
  };
}
